import { BaseApiClient } from './base.client';

/** `undefined` clientIp → no extra headers (avoids sending an empty/garbage `X-Forwarded-For`). */
function forwardedForOpts(clientIp?: string): { headers: Record<string, string> } | undefined {
  return clientIp ? { headers: { 'x-forwarded-for': clientIp } } : undefined;
}

// ─── Universal Price Object (Phase 0.12) ───────────────────────────────────

/**
 * Per-rate price object emitted by every supplier adapter.
 * Mirrors the backend `RateLevelUPO` interface (additive alongside legacy scalars).
 */
export interface RateLevelUPO {
  /** Total stay price, gross, in property currency (ex selected-room-count). */
  basePrice: number;
  /** Promotional price if the supplier offers a reduced rate. */
  promotionalBasePrice?: number;
  /** Ex-VAT price (basePrice minus supplier VAT). */
  exVatPrice?: number;
  /** Price per night (basePrice / nights). */
  pricePerNight: number;
  /** Genuine per-night base prices (RateHawk only); null/absent when the supplier quotes only a stay total. [F-PRICE-14] */
  nightlyPriceBreakdown?: number[] | null;
  /** Number of nights this rate covers. */
  nights: number;
  /** ISO 4217 property currency code. */
  propertyCurrency: string;
  /** true when VAT is already baked into basePrice (BCP IsVatIncludedInBAR). */
  vatIncluded: boolean;
  /** Supplier VAT percentage (0 when not applicable). */
  vatPct: number;
  /** true for BCP / HG / WH whose commission is handled separately. */
  isNegotiatedOrRetail: boolean;
  /** Our hotel commission rate (%). Filled by RateFinalizerService. */
  commissionRate: number;
  /** VAT rate on our commission (%). */
  commissionVatRate: number;
  /** Our commission amount (property currency). */
  commissionAmount: number;
  /** VAT on our commission (property currency). */
  vatOnCommission: number;
  /** basePrice + commissionAmount + vatOnCommission. */
  totalPriceWithCommission: number;
  /** totalPriceWithCommission + affiliateCommission. */
  totalPriceWithAllCommissions: number;
  /** The canonical price to charge the customer. */
  priceToCharge: number;
  /** Supplier name (bookingclap | ratehawk | ratehawk-cug | hyperguest | webhotelier). */
  supplierName: string;
}

/**
 * Per-rate, post-selectedCount aggregated price (for cart/booking use).
 * Extends RateLevelUPO with room-count-scaled monetary fields.
 */
export interface RoomBookingUPO extends RateLevelUPO {
  selectedCount: number;
  totalBasePriceAllRooms: number;
  totalPriceWithCommissionAllRooms: number;
  priceToChargeAllRooms: number;
}

/**
 * Canonical cancellation policy entry (Phase 0.2).
 */
export type CancellationChargeType = 'free' | 'percentage' | 'nights' | 'fixed' | 'nonRefundable';

export interface CancellationPolicyEntry {
  /** ISO 8601 or null (open-ended). */
  fromDate: string | null;
  toDate: string | null;
  chargeType: CancellationChargeType;
  /** The charge amount (percentage, nights, or fixed amount depending on chargeType). */
  chargeAmount: number;
  /** ISO 4217 currency code (for fixed charges). */
  chargeCurrency: string;
  /** Human-readable description. */
  description: string;
  /** ISO 8601 timestamp — free cancellation is allowed up to this point. */
  freeCancellationBefore: string | null;
}

// ─── Search types ──────────────────────────────────────────────────────────

export interface HotelSearchParams {
  dest_id?: string;
  venueId?: string;
  checkin: string;
  checkout: string;
  adults: string;
  children?: string;
  rooms?: string;
  ratecode?: string;
  userCurrency?: string;
  nationality?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  stars?: string;
  minPrice?: number;
  maxPrice?: number;
  meals?: string;
  facilities?: string;
  propertyTypes?: string;
  freeCancellationOnly?: boolean;
  maxDistanceMiles?: number;
  maxAffiliateDistanceMiles?: number;
  maxRefVenueDistanceMiles?: number;
  refLat?: number;
  refLon?: number;
  refLabel?: string;
  refSortLabel?: string;
}

export interface HotelNearbyGeoParams {
  lat: number;
  lon: number;
  radiusMiles?: number;
  checkin: string;
  checkout: string;
  adults: string;
  children?: string;
  rooms?: string;
  userCurrency?: string;
  nationality?: string;
  page?: number;
  pageSize?: number;
}

export interface FinalRate {
  /** Charged, VAT-inclusive regular total — feeds the commission multiplier and every displayed price. */
  totalBasePrice: number;
  /** Fixed raw supplier base price (never VAT-adjusted) — verification only. */
  RateFromSource: number;
  /** Regular (undiscounted) ex-VAT total — verification only. */
  PriceExTax: number;
  /** Regular (undiscounted) VAT total — verification only. */
  TaxFromSource: number;
  /** Money saved by any active promo — verification only. */
  DiscountAmount: number;
  /** Alias of totalBasePrice — named for the base → exVatPrice → priceIncludingVat flow. */
  priceIncludingVat: number;
  totalPromotionalPrice?: number;
  currency: string;
  commissionRate: number;
  commissionVatRate: number;
  commissionAmount: number;
  vatOnCommission: number;
  totalCommission: number;
  totalPriceWithCommission: number;
  // Populated by ResponseBuilderService per-request (user-currency conversion of totalPriceWithCommission/totalPromotionalPrice) — not part of the cached raw rate data.
  totalPriceInUserCurrency?: number;
  totalPromotionalPriceInUserCurrency?: number;
}

export interface HotelListRecord {
  id: string;
  mongoId: string;
  source: string;
  name: string;
  kind: string;
  address: string;
  city: string;
  state?: string;
  country?: string;
  latitude: number;
  longitude: number;
  starRating: number;
  /** Pre-rendered star-rating markup (CMS parity) — '' when starRating is 0/falsy. Always present
   *  from our own hotels-service response; optional here only because this type is also used to
   *  type-map other domains' third-party integration payloads, which don't carry it. */
  starRatingHtml?: string;
  pageUrl: string;
  primaryImage?: string;
  images: string[];
  amenities: string[];
  distanceFromCityCenter?: number;
  distanceFromVenue?: number;
  distanceFromGeo?: number;
  distanceFromAffiliateVenue?: number;
  distanceFromRefVenue?: number;
  detailPageUrl?: string;
  currency: string;
  currencySymbol: string;
  finalRate: FinalRate;
  recommendedRooms: RecommendedRoom[];
  hasFreeCancellation: boolean;
  hasVouchers: boolean;
  hasNegotiatedRate: boolean;
  affiliateMessage?: string;
  totalPriceInUserCurrency?: number;
  userCurrency?: string;
  score: number;
  searchResultsPageUrl: string;
}

export interface AffiliateVenueMeta {
  lat: number;
  lon: number;
  filterLabel?: string;
  sortLabel?: string;
  showFilter: boolean;
  showSorting: boolean;
}

export interface RefVenueMeta {
  lat: number;
  lon: number;
  label?: string;
  sortLabel?: string;
  showFilter: boolean;
  showSorting: boolean;
}

export interface FacetOption<T = string> {
  value: T;
  count: number;
}

export interface SearchListResponse {
  records: HotelListRecord[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
  filters?: {
    priceRange: { min: number; max: number; currency: string };
    availableStars: FacetOption<number>[];
    availableMeals: FacetOption<string>[];
    availableFacilities: FacetOption<string>[];
    availablePropertyTypes: FacetOption<string>[];
    availablePropertyChains: FacetOption<string>[];
    availableRoomTypes: FacetOption<string>[];
    availableDistances: FacetOption<number>[];
    availableAffiliateDistances?: FacetOption<number>[];
    availableRefVenueDistances?: FacetOption<number>[];
    freeCancellationCount: number;
  };
  affiliateVenueMeta?: AffiliateVenueMeta;
  refVenueMeta?: RefVenueMeta;
  currencyCode: string;
  currencySymbol: string;
  searchResultsPageUrl?: string;
  /** The sort actually applied to `records` (a `sortBy` URL-param string, e.g. 'recommended' |
   *  'distanceVenue' | ...). On a valid-affiliate landing with no explicit `sortBy` this is
   *  'distanceVenue', not 'recommended' — use it (not a client-side guess) to pick the initially
   *  active sort-dropdown option. */
  appliedSortBy?: string;
  fallbackMeta: {
    isFallbackApplied: boolean;
    radiusMiles?: number;
    bannerMessage?: string;
    /** Searched-city name (fallback only) — drives the "{cityName} city centre" card/filter label. */
    cityName?: string;
  };
  meta: {
    traceId: string;
    suppliers: Array<{ name: string; count: number; durationMs: number }>;
    cacheHit: boolean;
    searchDurationMs: number;
  };
}

// ─── Suggestion types ───────────────────────────────────────────────────────

export interface HotelSuggestion {
  id: string;
  label: string;
  label1?: string;      // canonical name; used by the ES grouped dropdown
  type: string;
  cityName?: string;    // used by the ES grouped dropdown
  stateName?: string;
  country?: string;
  regionId?: string;
  destinationId?: string;
  entityId?: string; // hotel's MongoDB _id; present on property-type entries
  pageUrl?: string; // Page_Url path; present on property-type entries — navigate to detail page directly
}

export interface Suggestions {
  citySuggestions: HotelSuggestion[],
  hotelSuggestions: HotelSuggestion[]
}

// ─── Featured / home types ──────────────────────────────────────────────────

export interface FeaturedHotel {
  id: string;
  supplierId: string;
  name: string;
  city: string;
  country?: string;
  starRating: number;
  primaryImage?: string;
  pageUrl?: string;
}

// ─── Detail page types ──────────────────────────────────────────────────────

export interface RecommendedRoom {
  rateId: string;
  roomId?: string;
  ratePlanName?: string;
  promotionName?: string;
  roomName: string;
  occurrence?: number;
  bedding?: string;
  /** BCP recommended-block bed line: "{BedType}: {Availability}" joined (e.g. "Double: 12"). */
  formattedBedDetails?: string;
  /** Room-level description shown in the "Show Room Information" modal (CMS parity). */
  description?: string;
  roomImages?: string[];
  roomAmenities?: string[];
  /** Room amenities grouped by category for the "Show room information" popup (BCP/HG/WH; CMS parity).
   *  Empty/absent → render the flat `roomAmenities` list (RateHawk). */
  roomAmenityGroups?: Array<{ groupName: string; amenities: string[] }>;
  boardBasis: string;
  mealDescription?: string;
  cancellationPolicy: string;
  freeCancellation?: boolean;
  freeCancellationBefore?: string;
  isNonRefundable: boolean;
  /** Date-active CASE-2 "Chargeable" state: past the free window but the active charge is < the stay
   *  total → a partial cancellation charge applies. The listing shows a name-only badge from this
   *  (no amount); the detail page shows the resolved amount instead. */
  cancellationChargeable?: boolean;
  noPrepaymentRequired?: boolean;
  /** Supplier cancellation-summary for the policy (i) tooltip (CMS PolicySummary). May contain
   *  `<br>`. Set for BCP + WH; empty for HG/RH (CMS shows no (i) there). */
  policySummary?: string;
  /** Prepayment line shown when set ("Advance payment required") — CMS sets it only for
   *  non-refundable rates, independent of the refundability block. */
  prepaymentPolicy?: string;
  /** Full prepayment policy for the prepayment (i) tooltip (CMS PrepaymentPolicySummary). */
  prepaymentPolicySummary?: string;
  /** Partial-charge cancellation cost in PROPERTY currency, resolved from the slabs against the
   *  charged stay total (R3/P2-10). The client converts to user currency for the "Cancellation:
   *  {amount}" line. Absent when there's no resolvable charge. */
  cancellationCharge?: number;
  /** HG detail-page ONLY: the single applicable cancellation STATEMENT line for a multi-step
   *  HyperGuest charge policy (the stage the booking date currently falls into). Undefined for other
   *  sources / single-step rates → the detail page falls back to the full `policySummary`. No amount,
   *  no date, no (i) — statement text only. */
  cancellationStatement?: string;
  hasVoucher: boolean;
  voucherHtml?: string;
  basePrice: number;
  finalPrice: number;
  promotionalFinalPrice?: number;
  /** CMS "NP": precise per-unit charged price (property ccy, UNROUNDED). Multiply by the room count
   *  and round ONCE for totals (sidebar/booking) so they reconcile with the recommended headline and
   *  the carted charge — never multiply the rounded `finalPrice`/`promotionalFinalPrice`. */
  chargedUnitPrice?: number;
  currency: string;
  currencySymbol: string;
  pricePerNight: number;
  taxes?: Array<{ name: string; amount: number; currency: string; status?: string }>;
  totalPriceInUserCurrency?: number;
  maxOccupancy?: number;
  /** Rate availability (rooms left for this rate) — caps the detail-page room counter "+" (R4). */
  availableCount?: number;
  cotAvailability?: string;
  extraBedAvailability?: string;
  /** BCP "Free child stay" (StayType === 'Free') — feature line in the recommended block (P2-13). */
  freeChildStay?: boolean;
  /** Phase 0.12: per-rate Universal Price Object (additive alongside legacy scalars). */
  price?: RateLevelUPO;
  /** Phase 0.12: structured cancellation policies (replaces per-source string shapes). */
  cancellationPoliciesNormalised?: CancellationPolicyEntry[];
  /** ISO 8601 timestamp when a last-minute deal expires (= check-in date+time). BCP only. */
  dealEndsAt?: string;
}

export interface PropertyDetailResponse {
  sessionId: string;
  entityId: string;
  supplierId: string;
  source: string;
  name: string;
  starRating: number;
  address: string;
  fullAddress?: string;
  city: string;
  country: string;
  phone?: string | null;
  email?: string | null;
  latitude?: number;
  longitude?: number;
  primaryImage?: string | null;
  images: string[];
  summary?: string | null;
  amenityGroups: Array<{ groupName: string; amenities: string[] }>;
  policies: Array<{ title: string; paragraph: string }>;
  /** BCP-only property policy rows (PolicyName/PolicyDescription) shown in the Policies table,
   *  distinct from `policies`. Empty/absent for RH/HG/WH (CMS detail-page-data.js:519, hotels.html:2044). */
  otherPolicies?: Array<{ title: string; paragraph: string }>;
  policyExtraInfo?: string | null;
  childrenPolicy?: string | null;
  cotPolicy?: string | null;
  paymentMethods: string[];
  checkinDate: string;
  checkoutDate: string;
  nights: number;
  occupancySummary: string;
  currency: string;
  currencySymbol: string;
  /** User-currency booking total (= converted finalRate.totalPriceWithCommission). Mirrors the
   *  listing-card field so the detail "recommended" headline matches the search-results price. */
  totalPriceInUserCurrency?: number;
  userCurrency?: string;
  userCurrencySymbol?: string;
  recommendedRooms: RecommendedRoom[];
  allRooms?: RecommendedRoom[];
  finalRate: FinalRate;
  checkinTime?: string | null;
  checkoutTime?: string | null;
  meta: {
    traceId: string;
    cacheHit: boolean;
    searchDurationMs?: number;
  };
}

export interface PropertyDetailParams {
  checkin: string;
  checkout: string;
  adults: string;
  children?: string;
  ratecode?: string;
  /** Masked affiliate code (BCP-853) — decrypted to ratecode server-side. */
  bip_k?: string;
  bip_l?: string;
  userCurrency?: string;
  nationality?: string;
}

// ─── Cart types ─────────────────────────────────────────────────────────────

export interface SelectedRoomRateInput {
  ratePlanId: string;
  selectedCount: number;
  RoomId?: string;
  RateId?: string;
  IsNegotiatedRate?: boolean;
}

export interface PrepareCartBody {
  sessionId: string;
  source: string;
  selectedRoomRates: SelectedRoomRateInput[];
  cartExpirationLink?: string;
}

export type PrepareCartResult = { cartId: string } | { error: string };

export interface AccessibilityRequests {
  wheelchair?: boolean;
  notes?: string;
}

export interface CotAndExtraBedItem {
  roomId: string;
  roomName: string;
  extraCot?: boolean;
  extraBed?: boolean;
  extraBedCount?: number;
  /** "Or" when cot + extra-bed are mutually exclusive for this room; any other string otherwise */
  dataValue?: string;
}

/** G4: a single cot / extra-bed pricing band by child age (BCP feed). */
export interface CotExtraBedBand {
  AgeFrom?: number;
  AgeTo?: number;
  /** "Free" | "Fixed" | "Percentage" | … — when "Free" no amount is shown. */
  StayPriceType?: string;
  StayAmount?: number;
  StayType?: string;
}

/**
 * Cart-level cross-room aggregate (`cart.finalRatesObj`), summed from every selected room's
 * `ratesObj` (backend `CartService.buildFinalRatesObj`). Loosely typed (index signature) since it
 * carries whatever numeric `ratesObj` fields exist — the named fields below are the ones the
 * reference-sheet price breakdown (BookingClap Guest Details/Payment sidebar) reads explicitly.
 */
export interface CartFinalRatesObj {
  /** Ex-VAT price, summed across rooms. */
  exVatPrice?: number;
  /** VAT/tax from source, summed across rooms. */
  vatAmount?: number;
  /** BIP (hotel) commission rate — representative single value, not summed. */
  hotelCommission?: number;
  /** BIP commission amount ex tax, summed across rooms. */
  commissionAmount?: number;
  /** VAT rate on BIP commission — representative single value, not summed. */
  vatOnHotelCommission?: number;
  /** VAT amount on BIP commission, summed across rooms. */
  vatOnCommission?: number;
  /** Cost/transaction commission rate — representative single value, not summed. */
  costCommissionRate?: number;
  /** VAT rate on cost commission — representative single value, not summed. */
  costCommissionVatRate?: number;
  /** Cost commission amount ex tax, summed across rooms. */
  costCommissionAmount?: number;
  /** VAT amount on cost commission, summed across rooms. */
  vatOnCostCommission?: number;
  /** exVatPrice + commissionAmount + costCommissionAmount, summed across rooms. */
  totalPriceExTax?: number;
  /** vatAmount + vatOnCommission + vatOnCostCommission, summed across rooms. */
  totalTax?: number;
  [key: string]: any;
}

export interface CartData {
  /** Mongo `_id` (hex string) of the Booking Cart. Present on every persisted cart from `getCart`. The
   *  CMS booking-confirmation page loads the booking by this id (`?cartId=<_id>`), NOT the UUID `cartId`. */
  _id?: string;
  cartId: string;
  source: string;
  /** Supplier property id (BCP numeric string; also used by HG/WH). Absent on an expired/empty cart. */
  PropertyId?: string;
  /** Back-to-search-results link captured at cart creation; used by the session-expired "Take me back" CTA. */
  cartExpirationLink?: string;
  propertyName: string;
  propertyCity: string;
  propertyCountry: string;
  /** Property state/region (CMS `propertyState`); appended to the non-BCP sidebar address. */
  propertyState?: string;
  /** Full property street address (CMS `propertyaddress`); falls back to city/country when blank. */
  propertyaddress?: string;
  /** ONE display-ready FULL address (CMS common field `fullAddress`), built source-conditionally at
   *  cart creation and shown identically on every surface for all sources. */
  fullAddress?: string;
  propertyPrimaryImage?: string;
  starRating?: number;
  /** Property contact (BCP only — other sources keep these blank). Aggregated from the
   *  showOnWebsite `Contact[]` feed with `+countrycode` prefixes (CMS get-cart-data.js:182-206). */
  phone?: string;
  mobile?: string;
  email?: string;
  memberId?: string;
  checkinDate: string;
  checkoutDate: string;
  nights: number;
  checkinTime?: string;
  checkoutTime?: string;
  propertyCurrency: string;
  userCurrency?: string;
  CurrencySymbol?: string;
  totalPrice: number;
  totalPriceInUserCurrency?: number;
  SelectedRoomRates: any[];
  finalPaymentSchedule: any;
  finalRatesObj: CartFinalRatesObj;
  allRatesNonRefundable?: boolean;
  vatPercentage?: number;
  vatAmount?: number;
  vatAmountInUserCurrency?: number;
  /** Additional taxes/fees merged across rooms by (name, status) (CMS `finalTaxList`). VAT-named
   *  entries are kept in the data but filtered at display (VAT shows as its own line). */
  finalTaxList?: Array<{ name: string; amount: number; currency?: string; status?: string }>;
  affiliateData?: any;
  OtherPolicy?: string;
  CotPolicy?: string;
  ChildrenPolicy?: string;
  /** Property-currency glyph (e.g. "£"). Cot/extra-bed amounts are paid at the property, so they
   *  render with this symbol, not the user-currency one. */
  PropertyCurrencySymbol?: string;
  /** G4: per-age cot pricing bands (BCP only). Empty for other sources. */
  cotDetail?: CotExtraBedBand[];
  /** G4: per-age extra-bed pricing bands (BCP only). Empty for other sources. */
  extraBedDetail?: CotExtraBedBand[];
  /** HTML summary shown in the Step-4 "Read full cot and extra bed policy" modal. */
  cotandnedPolicysummary?: string;
  policyExtraInfo?: string;
  policies?: Array<{ title: string; paragraph: string }>;
  /** Flattened amenity names from the hotel doc (populated at cart create). */
  amenities?: string[];
  /** Raw facilities from the hotel doc. */
  facilities?: any[];
  occupancySummary?: string;
  stayDetailsSummary?: string;
  status: string;
  sessionEnd: string;
  member?: any;

  // ── Guest-details extras (populated after POST /:cartId/guest) ────────────
  guestDetails?: Record<string, any>;
  specialRequests?: string;
  accessibilityRequests?: AccessibilityRequests;
  cotAndExtraBedData?: CotAndExtraBedItem[];

  // ── Payment fields (populated after /initiate-payment + /save-payment) ──
  stripeCustomerId?: string;
  stripePaymentIntentId?: string;
  stripeSetupIntentId?: string;
  paymentMethodId?: string;
  paymentMethodCountry?: string;
  paymentStatus?: string;
  intentType?: 'payment_intent' | 'setup_intent';
  /** Stripe intents previously minted for this cart, superseded but still valid for /save-payment. */
  previousIntentIds?: string[];

  // ── RateHawk-only ────────────────────────────────────────────────────────
  partnerOrderId?: string;
  rhPaymentMethod?: string;
  itemId?: string;
  orderId?: string;
  isGenderSpecificationRequired?: boolean;

  // ── Universal Price Object (Phase 0.12) ─────────────────────────────────
  /** Property-level UPO aggregate across all selected rooms. */
  price?: RoomBookingUPO;

  // ── Reservation / confirmation ──────────────────────────────────────────
  /** Core BIP booking reference `BIP-{ts}-{rnd}`, minted with the cart; universal userReservationId fallback. */
  referenceId?: string;
  /** SaveReservation (BCP/HG/WH) / saveRhReservation (RH) id. */
  reservationId?: string;
  /** The ONE key for all user communication = reservationId else referenceId. */
  userReservationId?: string;
  /** Vendors' own booking ids, kept per-source for ops. */
  hgBookingId?: string;
  whBookingId?: string;
  bookedDate?: string;
  bookingFailureReason?: string;
}

/** One row in the manage-bookings list (PWS2; server-derived display fields). */
export interface BookingListItem {
  id: string;
  source: string;
  propertyName: string;
  propertyCity: string;
  propertyCountry: string;
  propertyPrimaryImage: string;
  /** User-facing Booking Confirmation Number (= reservationId ‖ referenceId ‖ _id). */
  userReservationId: string;
  checkinDate: string;
  checkoutDate: string;
  /** Pre-formatted stay range ("15 - 17 May 2026"). */
  formattedDate: string;
  nights: number;
  totalRooms: number;
  bookingDate: string;
  /** Total in the user's display currency (per-source resolved); null when unavailable. */
  showPrice: number | null;
  userCurrency: string;
  currencySymbol: string;
  /** Clean badge label: Confirmed | Completed | Cancelled | No show. */
  statusLabel: string;
  /** Booking-level cancellation policy for the card (CMS shows `Rates[0].PolicyName`). */
  cancellationState: 'free' | 'nonRefundable' | 'charge' | 'mixed' | 'none';
  /** Full-booking cancellation cost in the user's display currency (only when state === 'charge'). */
  cancellationCharge: number;
  /** Additional taxes/fees (VAT excluded) for the price (i) tooltip — RateHawk/HyperGuest only. */
  finalTaxList: Array<{ name: string; amount: number; currency: string; status?: string }>;
}

export interface BookingsListResponse {
  records: BookingListItem[];
  total: number;
  limit: number;
  page: number;
  totalPages: number;
}

/** Result of a member cancel request. `status: 'success' | 'failed'`. */
export interface CancelBookingResult {
  status: string;
  message?: string;
  propertyName?: string;
}

/** One chat message (PWS5; shape from BCP, timestamps localised server-side). */
export interface ChatMessage {
  messageBy?: string; // 'Guest' | 'Property'
  messageText?: string;
  isImageUrl?: boolean;
  isMessageSeen?: boolean;
  messageFile?: string;
  createdDateTime?: string;
  sendingTime?: string;
  [k: string]: any;
}

export interface ChatMessagesResponse {
  messages: ChatMessage[];
}

export interface SendChatMessageResult {
  status: string; // 'success' | 'failed'
  /** Presigned PUT URL for the attachment (when one was included). */
  signedUrl?: string | null;
}

/** Booking detail = the stored cart augmented with stage-2 display fields. */
export interface BookingDetail extends CartData {
  effectiveStatus?: string | null;
  statusLabel: string;
  cancellable: boolean;
  bookedDate?: string;
  currencyRateStatement?: string;
}

export interface GuestDetailsBody {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  countryCode?: string;
  country?: string;
  travelWork?: string;
  companyName?: string;
  vatNumber?: string;
  bookingForSelf?: string;
  additionalFirstName?: string;
  additionalLastName?: string;
  memberId?: string;
  specialRequests?: string;
  accessibilityRequests?: AccessibilityRequests;
  cotAndExtraBedData?: CotAndExtraBedItem[];
}

/** One "Shows near {property}" cross-sell card (confirmation page). `fromPrice` is in the user's
 *  display currency; `bookUrl`/`exploreUrl` are CMS deep links onto the shows site. */
export interface NearbyShowItem {
  id: string;
  name: string;
  pageUrl: string;
  image: string | null;
  reviewData: unknown;
  city: string;
  state: string;
  distanceMiles: number | null;
  fromPrice: number;
  userCurrency: string;
  userCurrencySymbol: string;
  bookUrl: string;
}

export interface NearbyShowsResult {
  shows: NearbyShowItem[];
  exploreUrl: string | null;
}

// ─── Client ─────────────────────────────────────────────────────────────────

export class HotelsClient extends BaseApiClient {
  constructor(baseUrl?: string) {
    const resolved = baseUrl || process.env.NEXT_PUBLIC_HOTELS_API_URL;
    if (!resolved) {
      throw new Error(
        'HotelsClient: NEXT_PUBLIC_HOTELS_API_URL is not set. ' +
          'Configure it in the .env file (see apps/web/.env.example) or pass an explicit baseUrl to the constructor.',
      );
    }
    super(resolved);
  }

  /**
   * Send the `member_d` session cookie on every hotels call so the member-scoped endpoints
   * (bookings / cancel / chat) can validate the session server-side (PWS2/3/5 hardening). Delegates
   * to the base `request` so all error/timeout handling is preserved; harmless on SSR/non-member calls.
   */
  protected override async request<T>(
    endpoint: string,
    options?: RequestInit & { next?: { revalidate?: number | false; tags?: string[] }; timeoutMs?: number },
  ): Promise<T> {
    return super.request<T>(endpoint, { ...(options || {}), credentials: 'include' });
  }

  /**
   * `clientIp` forwards the ORIGINAL visitor's IP (read from the incoming Next.js request's
   * own `X-Forwarded-For`, since this SSR call is server-to-server and otherwise carries no
   * trace of the browser) as `X-Forwarded-For` on the outbound request — hotels-service's
   * GeoIP-derived `userCountry` depends on it. See `apps/web/app/_lib/clientIp.ts`.
   */
  async search(params: HotelSearchParams, clientIp?: string): Promise<SearchListResponse> {
    return this.get<SearchListResponse>('/search', params as any, forwardedForOpts(clientIp));
  }

  async nearbyGeo(params: HotelNearbyGeoParams): Promise<SearchListResponse> {
    return this.get<SearchListResponse>('/search/nearby-geo', params as any);
  }

  /**
   * Cross-sell endpoint used by the sports checkout + booking-success
   * pages. Returns a `records` array of hotels nearest the supplied
   * lat/lng, with prices already pre-converted into `userCurrency`. No
   * pagination — just the top N records the service decides on.
   */
  async nearbyHotels(params: {
    latitude: number;
    longitude: number;
    userCurrency?: string;
    checkin?: string;
    checkout?: string;
  }): Promise<{ records: HotelListRecord[]; searchResultsPageUrl: string }> {
    // No client-side timeout — the endpoint fans out to several hotel
    // suppliers (BookingClap, RateHawk, HyperGuest, WebHotelier) and a
    // real query can legitimately take longer than any short timeout
    // we'd pick. We previously set 6 s; real requests were being
    // aborted before any supplier responded, leaving the cross-sell
    // card empty even when data WOULD have arrived. The NearbyHotels
    // component already uses a `cancelled` ref to ignore late results
    // after unmount, so leaving the network call uncancelled is safe.
    return this.get<{ records: HotelListRecord[]; searchResultsPageUrl: string }>(
      '/search/nearby-hotels',
      params as any,
      { timeoutMs: 60000 },
    );
  }

  /** See `search()` — `clientIp` forwards the visitor's real IP for GeoIP-derived `userCountry`. */
  async getDetail(
    entityId: string,
    params: PropertyDetailParams,
    clientIp?: string,
  ): Promise<PropertyDetailResponse> {
    return this.get<PropertyDetailResponse>(`/properties/${entityId}`, params as any, forwardedForOpts(clientIp));
  }

  /** LIVE default — flat MongoDB-backed suggestions. */
  async getSuggestions(q: string, limit = 5): Promise<HotelSuggestion[]> {
    return this.get<HotelSuggestion[]>('/suggestions', { q, limit });
  }

  /** PARALLEL/experimental — grouped Elasticsearch-backed suggestions ({ citySuggestions, hotelSuggestions }). */
  async getSuggestionsEs(q: string, limit = 5): Promise<Suggestions> {
    return this.get<Suggestions>('/suggestions/es', { q, limit });
  }

  async getLocationLabel(id: string): Promise<string | null> {
    try {
      const result = await this.get<{ label: string }>('/suggestions/label', { id });
      return result?.label ?? null;
    } catch {
      return null;
    }
  }

  async getLocationInfo(id: string): Promise<{
    label: string;
    countryCode?: string;
    cityName?: string;
    stateName?: string;
    country?: string;
  } | null> {
    try {
      const result = await this.get<{
        label: string;
        countryCode?: string;
        cityName?: string;
        stateName?: string;
        country?: string;
      }>('/suggestions/label', { id });
      return result ?? null;
    } catch {
      return null;
    }
  }

  async getFeatured(limit = 12): Promise<FeaturedHotel[]> {
    return this.get<FeaturedHotel[]>('/home/featured', { limit });
  }

  async prepareCart(body: PrepareCartBody): Promise<PrepareCartResult> {
    return this.post<PrepareCartResult>('/cart/prepare', body);
  }

  async getCart(cartId: string): Promise<CartData> {
    return this.get<CartData>(`/cart/${cartId}`);
  }

  async saveGuestDetails(cartId: string, body: GuestDetailsBody): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>(`/cart/${cartId}/guest`, body);
  }

  async releaseAvailability(cartId: string): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>(`/cart/${cartId}/release-availability`, {});
  }

  async initiatePayment(cartId: string): Promise<InitiatePaymentResult> {
    return this.post<InitiatePaymentResult>(`/cart/${cartId}/initiate-payment`, {});
  }

  async savePayment(cartId: string, body: SavePaymentBody): Promise<SavePaymentResult> {
    return this.post<SavePaymentResult>(`/cart/${cartId}/save-payment`, body);
  }

  async getBookingStatus(cartId: string): Promise<SavePaymentResult> {
    return this.get<SavePaymentResult>(`/cart/${cartId}/booking-status`);
  }

  /** Token-gated confirmation fetch. Throws when token is bad or cart isn't confirmed. */
  async getConfirmation(cartId: string, token: string): Promise<CartData> {
    return this.get<CartData>(`/cart/${cartId}/confirmation`, { token });
  }

  /** "Shows near {property}" cross-sell for the confirmation page. Never throws server-side —
   *  returns `{ shows: [], exploreUrl: null }` when the city has no matching shows. */
  async getNearbyShows(cartId: string): Promise<NearbyShowsResult> {
    return this.get<NearbyShowsResult>(`/cart/${cartId}/nearby-shows`);
  }

  /**
   * Member-scoped bookings list (PWS2). `status` ∈ 'Confirmed' | 'Cancelled' | '' (all).
   * `memberId` is the logged-in member's id (resolved from auth /me on the caller side).
   */
  async getBookings(params: {
    memberId: string;
    status?: string;
    page?: number;
    pagesize?: number;
  }): Promise<BookingsListResponse> {
    return this.get<BookingsListResponse>('/bookings', {
      memberId: params.memberId,
      ...(params.status ? { status: params.status } : {}),
      ...(params.page ? { page: String(params.page) } : {}),
      ...(params.pagesize ? { pagesize: String(params.pagesize) } : {}),
    });
  }

  /** Member-scoped booking detail (stage-2 confirmed view). `id` is the booking `_id`. */
  async getBookingDetail(id: string, memberId: string): Promise<BookingDetail> {
    return this.get<BookingDetail>(`/bookings/${id}`, { memberId });
  }

  /** Guest ↔ property chat (PWS5) — list messages for a booking (member-scoped). */
  async getChatMessages(
    cartId: string,
    memberId: string,
    opts?: { limit?: number; timeZone?: string },
  ): Promise<ChatMessagesResponse> {
    return this.get<ChatMessagesResponse>(`/chat/${cartId}/messages`, {
      memberId,
      ...(opts?.limit ? { limit: String(opts.limit) } : {}),
      ...(opts?.timeZone ? { timeZone: opts.timeZone } : {}),
    });
  }

  /** Send a guest chat message. Returns a presigned PUT URL when an attachment was included. */
  async sendChatMessage(
    cartId: string,
    body: {
      memberId: string;
      message?: string;
      isImageUrl?: boolean;
      createdDate?: string;
      fileData?: { filename: string; size: number; type: string };
    },
  ): Promise<SendChatMessageResult> {
    return this.post<SendChatMessageResult>(`/chat/${cartId}/messages`, body);
  }

  /**
   * Member-initiated cancel (PWS3). `id` = booking `_id`. Pass `propertyCancellation` to cancel the
   * whole booking, or `roomCancellation` + `roomId` for a BookingClap per-room cancel.
   */
  async cancelBooking(
    id: string,
    body: { memberId: string; roomId?: number; roomCancellation?: boolean; propertyCancellation?: boolean },
  ): Promise<CancelBookingResult> {
    return this.post<CancelBookingResult>(`/bookings/${id}/cancel`, body);
  }

  /**
   * Confirmation-page cancel — no member session. Authorised by the signed confirmation token + cartId
   * (the same token that gates the confirmation page; CMS cancels by cartId here). Pass
   * `propertyCancellation` for the whole booking, or `roomCancellation` + `roomId` for a BCP per-room cancel.
   */
  async cancelConfirmedBooking(
    body: { cartId: string; token: string; roomId?: number; roomCancellation?: boolean; propertyCancellation?: boolean },
  ): Promise<CancelBookingResult> {
    return this.post<CancelBookingResult>(`/bookings/cancel-confirmed`, body);
  }
}

export const hotelsClient = new HotelsClient();

// ─── Payment types ─────────────────────────────────────────────────────────

export interface InitiatePaymentResult {
  clientSecret: string;
  intentType: 'payment_intent' | 'setup_intent';
  status: string;
  reused: boolean;
}

export interface SavePaymentBody {
  intentType: 'payment_intent' | 'setup_intent';
  intentId: string;
}

export interface SavePaymentResult {
  status: string;
  reservationId?: string;
  userReservationId?: string;
  bookingFailureReason?: string;
  /** Short-lived signed token to access /confirmation. Absent on non-success. */
  confirmationToken?: string;
  /**
   * CMS booking-confirmation page URL. Present on every confirmed booking — the funnel FULL-navigates
   * here (CMS owns the post-booking confirmation page). Defaults to the domain-relative
   * `/booking-confirmation?cartId=<Mongo _id>` (so the domain stays dynamic); `CMS_CONFIRMATION_URL`
   * overrides the base with a full absolute URL for a cross-origin CMS.
   */
  confirmationRedirectUrl?: string;
  /** RH only: client must poll `/booking-status` to finalise. */
  pollBookingStatus?: boolean;
}
